// @ts-strict-ignore
import {Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, output} from "@angular/core";
import {TrainingRoomNavigationPanelBase} from "../base/training-room-navigation-panel-base";
import {
	ActivityState,
	ITrainingRoomWhitelist,
	Modules,
	OccupationState,
	PlayerMapping,
	PlayerStatus,
	RoomCapability,
	TagType,
	TrainingRoom,
	TrainingRoomGroup,
	TrainingRoomType,
} from "@models";
import {MatTabChangeEvent} from "@angular/material/tabs";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {SimConfigurationService, SimUtilityService} from "@services";
import {EventEmitterService} from "@event-emitter";
import {UntilDestroy} from "@ngneat/until-destroy";
import {DigitalSignageDraggedItemType} from "../../../../modules/web-manager/subModules/digital-signage/pages/digital-signage-configuration-page/digital-signage-configuration-page.component";

export enum TrainingRoomTypeVM {
	Physical,
	Virtual,
	TrainTheTrainer,
}

@UntilDestroy()
@Component({
	selector: "sim-training-room-navigation-panel",
	templateUrl: "./training-room-navigation-panel.component.html",
	styleUrls: ["./training-room-navigation-panel.component.scss"],
	providers: [EventEmitterService],
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class TrainingRoomNavigationPanelComponent extends TrainingRoomNavigationPanelBase implements OnInit, OnDestroy, OnChanges {
	// Input
	@Input() selectedTrainingRooms: TrainingRoom[] = [];
	@Input() viewedInRoomPlanner: boolean = false;
	@Input() showNonSimstationRooms: boolean = false;
	@Input() singleRoom = false;
	@Input() expandAllCards = false;
	@Input() enableTabHeaderHidden = true;
	@Input() collapseAllCards = false;
	@Input() selectGroupDeselectRoom = false;
	@Input() enableDraggingRooms = false;
	@Input() disableIfNoOnlineDebriefingPlayers = false;
	@Input() fromSignageView = false;
	@Input() connectedLists: string[] = [];
	@Input() disableSelection: boolean = false;
	@Input() autoSelectIfSingular: boolean = false;
	@Input() planView = false;
	@Input() planViewReadOnly = false;
	@Input() triggerTrainingRoomClickedFromProSelection: boolean = false;
	@Input({required: true}) trainingRoomWhitelist: ITrainingRoomWhitelist = null;

	// Output
	onRoomGroupSelected = output<TrainingRoomGroup>();
	onRoomTypeTabChanged = output<TrainingRoomTypeVM>();

	// Enums
	PlayerStatus = PlayerStatus;
	TrainingRoomType = TrainingRoomType;
	RoomCapability = RoomCapability;
	Modules = Modules;
	DraggedItemType = DigitalSignageDraggedItemType;

	// Properties
	public hasLicenseModule = SimConfigurationService.hasLicenseModule;
	public selectedTabIndex = 0;
	public roomIsSelected = false;
	public draggingTrainingRoom: TrainingRoom = undefined;
	protected readonly TagType = TagType;
	isFilterOpen: boolean;

	constructor(injector: Injector) {
		super(injector);

		this.filteredRooms = this.rawRooms;

		//Update online status of Players
		this._emitterService.onPlayerStateChanged.subscribe((data) => {
			if (!this.rawRooms) return;
			for (const playerMapping of this.rawRooms.flatMap((trainingRoom) =>
				trainingRoom.players.concat(trainingRoom.digitalSignagePlayers, trainingRoom.debriefingPlayers, trainingRoom.mediaScreens),
			)) {
				if (playerMapping.id == data.player.id) {
					SimUtilityService.copy(data.player, playerMapping);
				}
			}
		});
	}

	public isOtherTrainingRoomGroupSelected(): boolean {
		const selectedGroup = this.rawRoomGroups[this.selectedTabIndex];
		return this.selectedTabIndex === this.rawRoomGroups.length - 1 && selectedGroup?.id === undefined;
	}

	public isGroupOtherGroup(group: TrainingRoomGroup): boolean {
		return group?.id === undefined;
	}

	public async ngOnInit(): Promise<void> {
		await super.ngOnInit();

		// if we have exactly one currently unoccupied room with a single, online player -> preselect it
		if (!this.autoSelectIfSingular || this.rawRooms.length !== 1) return;
		const singleDebriefingRoom = this.rawRooms[0];
		if (
			singleDebriefingRoom.occupiedTrainingRoom?.occupationState !== OccupationState.Occupied &&
			singleDebriefingRoom.players.length == 1 &&
			singleDebriefingRoom.players[0].webSocket.activityState == ActivityState.Active
		) {
			this.onTrainingRoomClicked.emit(singleDebriefingRoom);
		}
	}

	public async fireTrainingRoomClicked(trainingRoom: TrainingRoom, checked: boolean): Promise<void> {
		if (this.disableSelection) return;

		if (!this.triggerTrainingRoomClickedFromProSelection) {
			await super.fireTrainingRoomClicked(trainingRoom, checked);
		}

		if (this.singleRoom) {
			this.selectedTrainingRooms = [];
			if (this.selectGroupDeselectRoom) {
				this.roomIsSelected = !this.roomIsSelected;
			}
		}

		if (checked) {
			for (let i = 0; i < this.selectedTrainingRooms.length; i++) {
				if (this.selectedTrainingRooms[i].id === trainingRoom.id) {
					this.selectedTrainingRooms.splice(i, 1);
					break;
				}
			}
		} else {
			this.selectedTrainingRooms.push(trainingRoom);
		}

		if (this.triggerTrainingRoomClickedFromProSelection) {
			await super.fireTrainingRoomClicked(trainingRoom, checked);
		}
	}

	public getButtonState(
		trainingRoom: TrainingRoom & {
			selectedPlayer?: PlayerMapping;
		},
	): "normal" | "occupied" | "debriefing" | "unavailable" | "selected" {
		if (this.allowSelectionWhenOccupied) {
			return "normal";
		}

		if (trainingRoom.occupiedTrainingRoom?.occupationState === OccupationState.Occupied) {
			return "occupied";
		} else if (
			trainingRoom.players.filter((player) => [PlayerStatus.LiveStream, PlayerStatus.Recording].includes(player.info.status)).length >
			0
		) {
			return "occupied";
		} else if (
			trainingRoom.occupiedTrainingRoom?.occupationState === OccupationState.Selected &&
			!SimUtilityService.isAnyClient(this._clientProperties, trainingRoom.occupiedTrainingRoom?.webClientInfoId)
		) {
			return "selected";
		} else if (trainingRoom.selectedPlayer && trainingRoom.selectedPlayer.info.status === PlayerStatus.Debriefing) {
			return "debriefing";
		} else if (
			this.roomCapability === RoomCapability.Debriefing &&
			this.disableIfNoOnlineDebriefingPlayers &&
			this.allDebriefingPlayersOffline(trainingRoom)
		) {
			return "unavailable";
		} else {
			return "normal";
		}
	}

	public deselectAllRooms(): void {
		this.selectedTrainingRooms = [];
	}

	public getChecked(trainingRoom: TrainingRoom): boolean {
		for (const selectedTrainingRoom of this.selectedTrainingRooms) {
			if (selectedTrainingRoom?.id === trainingRoom.id) {
				return true;
			}
		}
		return false;
	}

	public groupChanged($event: MatTabChangeEvent): void {
		this.onRoomGroupSelected.emit(this.rawRoomGroups[$event.index]);
		if (this.selectGroupDeselectRoom && this.selectedTrainingRooms.length > 0) {
			this.roomIsSelected = false;
			this.selectedTrainingRooms = [];
			this.onTrainingRoomClicked.emit(null);
		}

		setTimeout(() => {
			this.selectedTabIndex = $event.index;
		});
	}

	public tabClick(event: MouseEvent): void {
		let el = event.target as HTMLElement;
		const attr = el.attributes.getNamedItem("class");
		if (!attr) {
			return;
		}

		if (attr.value.indexOf("mat-tab-label-content") >= 0) {
			el = el.parentElement;
		}

		const tabIndex = el.id.substring(el.id.length - 1);
		if (parseInt(tabIndex) === this.selectedTabIndex) {
			// active tab clicked
			if (this.selectGroupDeselectRoom && this.selectedTrainingRooms.length > 0) {
				this.roomIsSelected = false;
				this.selectedTrainingRooms = [];
				this.onTrainingRoomClicked.emit(null);
			}
		}
	}

	public isRoomDragging(trainingRoom: TrainingRoom): boolean {
		return this.draggingTrainingRoom?.id === trainingRoom.id;
	}

	public clickedTest(): void {
		if (this.draggingTrainingRoom) this.draggingTrainingRoom = undefined;
	}

	public reloadRooms(): void {
		super.loadTrainingRooms().then();
	}

	public allDebriefingPlayersOffline(trainingRoom: TrainingRoom & {selectedPlayer?: PlayerMapping}): boolean {
		return !trainingRoom.debriefingPlayers.some(
			(debriefingPlayer) => debriefingPlayer?.webSocket?.activityState === ActivityState.Active,
		);
	}

	public roomTypeTabChanged($event: MatTabChangeEvent): void {
		// We can use tabIndex because it corresponds to the int value of the enum
		this.onRoomTypeTabChanged.emit($event.index);
	}

	public openCreateVirtualRoomDialog(): void {
		this._simDialogService.openCreateVirtualRoomDialog({}).then((result) => {
			if (result?.action !== "ok") return;
			this._simNotificationService.notifySuccess(this.translate.instant(this.T.SIM_ROOM_PLANNER_VIRTUAL_ROOM_CREATED));
			this.reloadRooms();
		});
	}

	areGroupsEmpty(): boolean {
		return this.filteredRoomGroups.every((group) => group.trainingRooms.length === 0);
	}
}
